import { Box } from "@chakra-ui/react";
import { Empty } from "antd";
import InfiniteScroll from "react-infinite-scroll-component";
import { VariableSizeList as WindowList } from "react-window";
import React, { useRef, useLayoutEffect, useCallback } from "react";
import { WindowScroller } from "react-virtualized";
import InfiniteScrollLoader from "components/InfiniteScrollLoader";

interface InfiniteScrollCardListProps<T> {
  data?: T[];
  hasMore: boolean;
  onNext: () => any;
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
  emptyDescription?: string;
  RenderCard: ({ item, index }: { item: T; index: number }) => JSX.Element;
  defaultCardSize?: number;
  listHeight?: number;
  itemMarginBottom?: number;
}

const WindowListItem = <T,>({
  index,
  style,
  data,
  onItemHeightSet,
  RenderCard,
}: {
  index: number;
  style: React.CSSProperties;
  data: T[];
  onItemHeightSet: (index: number, height: number) => void;
  RenderCard: ({ item, index }: { item: T; index: number }) => JSX.Element;
}) => {
  const divRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (divRef.current) {
      onItemHeightSet(index, divRef.current.clientHeight);
    }
  }, [index, onItemHeightSet]);

  const _data = data[index];

  return (
    <div style={{ ...style }}>
      <div ref={divRef}>
        <RenderCard item={_data} index={index} key={index} />
      </div>
    </div>
  );
};

function InfiniteScrollCardList<T>({
  data,
  hasMore,
  onNext,
  isLoading,
  isFetchingNextPage,
  emptyDescription = "Sem itens para essa listagem",
  RenderCard,
  defaultCardSize = 200,
  listHeight = 800,
  itemMarginBottom = 12,
}: InfiniteScrollCardListProps<T>) {
  const windowListRef = useRef<any>();
  const rowHeights = useRef<Record<number, number>>({});

  const onItemHeightSet = useCallback((index: number, height: number) => {
    if (windowListRef.current) {
      windowListRef.current.resetAfterIndex?.(0);
    }

    rowHeights.current = { ...rowHeights.current, [index]: height };
  }, []);

  const getRowHeight = (index: number) => {
    return (
      rowHeights.current[index] + itemMarginBottom ||
      defaultCardSize + itemMarginBottom
    );
  };

  const handleWindowScroll = ({ scrollTop }: { scrollTop: number }) => {
    if (windowListRef.current) {
      windowListRef.current.scrollTo(scrollTop);
    }
  };

  const _WindowListItem = useCallback(
    ({
      index,
      style,
      data,
    }: {
      index: number;
      style: React.CSSProperties;
      data: T[];
    }) => (
      <WindowListItem
        index={index}
        style={style}
        data={data || []}
        onItemHeightSet={onItemHeightSet}
        RenderCard={RenderCard}
      />
    ),
    [onItemHeightSet, RenderCard]
  );

  return (
    <>
      <WindowScroller onScroll={handleWindowScroll}>
        {() => <div />}
      </WindowScroller>

      <Box overflowY="hidden">
        {!isLoading && !isFetchingNextPage && !data?.length ? (
          <Empty description={emptyDescription} />
        ) : null}

        {data?.length ? (
          <InfiniteScroll
            dataLength={data.length}
            next={onNext}
            hasMore={!!hasMore}
            loader={isFetchingNextPage ? <InfiniteScrollLoader /> : null}
          >
            <WindowList
              ref={windowListRef}
              className="window-scroller-override"
              itemData={data}
              height={listHeight}
              width={"100%"}
              itemCount={data.length}
              itemSize={getRowHeight}
            >
              {_WindowListItem}
            </WindowList>
          </InfiniteScroll>
        ) : null}
      </Box>
    </>
  );
}

export default InfiniteScrollCardList;
